import { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { SIRUC_INSPECTIONS } from "@/constants/testIds";
import { buildPoints } from "@/lib/inspectionPoints";
import { PhotoCapture } from "@/components/PhotoCapture";
import SignaturePad from "@/components/SignaturePad";
import { syncManager } from "@/lib/syncManager";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import { useNavigate, useSearchParams, Link, useParams } from "react-router-dom";
import { ArrowLeft, ClipboardCheck, Sparkles } from "lucide-react";

export default function SirucInspectionForm() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { inspId } = useParams();
  const qc = useQueryClient();

  const [type, setType] = useState("19_puntos");
  const [meta, setMeta] = useState({
    compania_transportista: "",
    placas_unidad: params.get("placas") || "",
    numero_trailer: "",
    numero_precinto: "",
    numero_precinto_na: false,
    sello_alta_seguridad: "",
    sello_verificado: false,
    inspector_nombre: "",
    actividad_sospechosa: "",
    firma_inspector: "",
    firma_chofer: "",
    tipo_caja: "53'",
    alto: "",
    ancho: "",
    largo: "",
    capacidad: "",
    supervisor_nombre: "",
    firma_supervisor: "",
  });
  const [points, setPoints] = useState(() => buildPoints("19_puntos"));
  const recordId = params.get("record_id") || null;

  const { data: existing } = useQuery({
    queryKey: ["inspection", inspId],
    queryFn: () => api.get(`/siruc/inspections/${inspId}`).then(r => r.data),
    enabled: !!inspId
  });

  useEffect(() => {
    if (existing) {
      setMeta({
        compania_transportista: existing.compania_transportista || "",
        placas_unidad: existing.placas_unidad || "",
        numero_trailer: existing.numero_trailer || "",
        numero_precinto: existing.numero_precinto || "",
        numero_precinto_na: existing.numero_precinto_na || false,
        sello_alta_seguridad: existing.sello_alta_seguridad || "",
        sello_verificado: existing.sello_verificado || false,
        inspector_nombre: existing.inspector_nombre || "",
        actividad_sospechosa: existing.actividad_sospechosa || "",
        firma_inspector: existing.firma_inspector || "",
        firma_chofer: existing.firma_chofer || "",
      });
      setType(existing.inspection_type || "19_puntos");
      setPoints(existing.points || buildPoints(existing.inspection_type || "19_puntos"));
    }
  }, [existing]);

  const onTypeChange = (t) => {
    setType(t);
    setPoints(buildPoints(t));
  };

  const updatePoint = (idx, patch) => {
    setPoints((prev) => prev.map((p, i) => (i === idx ? { ...p, ...patch } : p)));
  };

  const saveMutation = useMutation({
    mutationFn: (body) => inspId
      ? api.patch(`/siruc/inspections/${inspId}`, body).then(r => r.data)
      : api.post("/siruc/inspections", body).then((r) => r.data),
    onSuccess: (data, variables) => {
      qc.invalidateQueries({ queryKey: ["siruc-inspections"] });
      qc.invalidateQueries({ queryKey: ["siruc-record"] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(inspId ? "Inspección actualizada" : "Inspección guardada");
      if (variables?.status_general === "malo") feedback.warning(); else feedback.success();
      navigate("/siruc/inspections");
    },
    onError: (e) => { toast.error(apiError(e, "Error al guardar inspección")); feedback.error(); },
  });

  const submit = (e) => {
    e.preventDefault();
    if (!meta.placas_unidad) {
      toast.error("Las placas son obligatorias");
      return;
    }
    const payload = {
      ...meta,
      numero_precinto: meta.numero_precinto_na ? "N/A" : meta.numero_precinto,
      inspection_type: type,
      points,
      record_id: existing?.record_id || recordId,
      status_general: badCount > 0 ? "malo" : "bueno"
    };

    if (syncManager.isOffline()) {
      const method = inspId ? "PATCH" : "POST";
      const url = inspId ? `/siruc/inspections/${inspId}` : "/siruc/inspections";
      syncManager.addToQueue(url, method, payload, `Inspección ${meta.placas_unidad}`);
      navigate("/siruc/inspections");
      return;
    }

    saveMutation.mutate(payload);
  };

  const badCount = useMemo(() => points.filter((p) => p.estado === "malo").length, [points]);

  const ocrMutation = useMutation({
    mutationFn: (body) => api.post("/ocr/analyze", body).then(r => r.data),
    onSuccess: (data) => {
      setMeta(prev => ({
        ...prev,
        placas_unidad: data.placas_unidad || prev.placas_unidad,
        compania_transportista: data.compania_transporte || data.compania_transportista || prev.compania_transportista,
        numero_precinto: data.sello_seguridad || data.numero_precinto || prev.numero_precinto,
      }));
      toast.success("Datos extraídos");
    },
    onError: (e) => toast.error(apiError(e, "Error al analizar documento")),
  });

  const handleScan = (img) => ocrMutation.mutate({ image_b64: img, context: "inspection" });

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      <Link
        to="/siruc/inspections"
        className="flex items-center gap-2 text-sm text-muted-foreground hover:text-navy mb-4"
      >
        <ArrowLeft size={16} /> Volver a inspecciones
      </Link>

      <header className="mb-6">
        <h1 className="font-heading text-3xl font-extrabold tracking-tight text-navy">
          {inspId ? "Modificar inspección" : "Nueva inspección C-TPAT"}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Verificación de puntos de seguridad de la unidad.</p>
      </header>

      <form onSubmit={submit} className="space-y-5">
        <section className="bg-white border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-navy">Escaneo de documento (IA)</h2>
            {ocrMutation.isPending && <span className="text-xs text-navy animate-pulse font-bold">ANALIZANDO...</span>}
          </div>
          <div className="max-w-xs">
            <PhotoCapture
              single
              label="Escanear documento (Cámara o Galería)"
              onChange={(v) => handleScan(v[0])}
              testid="siruc-ia-scan"
            />
          </div>
        </section>

        <section className="bg-white border border-border p-5">
          <h2 className="font-heading text-lg font-bold text-navy mb-4">Datos generales</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            <Field label="Tipo de inspección *">
              <select
                data-testid={SIRUC_INSPECTIONS.formType}
                value={type}
                onChange={(e) => onTypeChange(e.target.value)}
                className="input"
              >
                <option value="19_puntos">19 puntos</option>
                <option value="9_puntos">9 puntos</option>
              </select>
            </Field>
            <Field label="Placas *">
              <input
                data-testid={SIRUC_INSPECTIONS.formPlacas}
                value={meta.placas_unidad}
                onChange={(e) => setMeta({ ...meta, placas_unidad: e.target.value })}
                className="input"
                required
              />
            </Field>
            <Field label="Compañía transportista">
              <input
                data-testid={SIRUC_INSPECTIONS.formCompania}
                value={meta.compania_transportista}
                onChange={(e) => setMeta({ ...meta, compania_transportista: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Número de trailer">
              <input
                data-testid={SIRUC_INSPECTIONS.formTrailer}
                value={meta.numero_trailer}
                onChange={(e) => setMeta({ ...meta, numero_trailer: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Número de precinto">
              <div className="flex items-center gap-2">
                <input
                  data-testid={SIRUC_INSPECTIONS.formPrecinto}
                  value={meta.numero_precinto}
                  disabled={meta.numero_precinto_na}
                  onChange={(e) => setMeta({ ...meta, numero_precinto: e.target.value })}
                  className="input disabled:opacity-40"
                />
                <label className="flex items-center gap-1.5 text-[10px] font-black uppercase text-navy/60 whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={meta.numero_precinto_na}
                    onChange={(e) => setMeta({ ...meta, numero_precinto_na: e.target.checked, numero_precinto: "" })}
                  />
                  N/A
                </label>
              </div>
            </Field>
            <Field label="Sello de alta seguridad">
              <input
                data-testid={SIRUC_INSPECTIONS.formSelloAlta}
                value={meta.sello_alta_seguridad}
                onChange={(e) => setMeta({ ...meta, sello_alta_seguridad: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Inspector">
              <input
                data-testid={SIRUC_INSPECTIONS.formInspector}
                value={meta.inspector_nombre}
                onChange={(e) => setMeta({ ...meta, inspector_nombre: e.target.value })}
                className="input"
              />
            </Field>
            <Field label="Supervisor que aprueba">
              <input
                value={meta.supervisor_nombre}
                onChange={(e) => setMeta({ ...meta, supervisor_nombre: e.target.value })}
                className="input"
              />
            </Field>

            <div className="col-span-full border-t border-border pt-4 mt-2">
              <p className="text-[10px] font-black text-navy/40 uppercase tracking-[0.2em] mb-3">Dimensiones de la Caja</p>
            </div>

            <Field label="Tipo de Caja">
              <input value={meta.tipo_caja} onChange={e => setMeta({...meta, tipo_caja: e.target.value})} className="input" />
            </Field>
            <Field label="Alto (m)">
              <input value={meta.alto} onChange={e => setMeta({...meta, alto: e.target.value})} className="input" />
            </Field>
            <Field label="Ancho (m)">
              <input value={meta.ancho} onChange={e => setMeta({...meta, ancho: e.target.value})} className="input" />
            </Field>
            <Field label="Largo (m)">
              <input value={meta.largo} onChange={e => setMeta({...meta, largo: e.target.value})} className="input" />
            </Field>
            <Field label="Capacidad (m³)">
              <input value={meta.capacidad} onChange={e => setMeta({...meta, capacidad: e.target.value})} className="input" />
            </Field>
            <Field label="Sello verificado">
              <label className="flex items-center gap-2 h-11 px-3 border border-border bg-white">
                <input
                  type="checkbox"
                  data-testid={SIRUC_INSPECTIONS.formSelloVerificado}
                  checked={meta.sello_verificado}
                  onChange={(e) => setMeta({ ...meta, sello_verificado: e.target.checked })}
                  className="w-4 h-4"
                />
                <span className="text-sm text-navy">Verificado</span>
              </label>
            </Field>
          </div>
          <Field label="Actividad sospechosa">
            <textarea
              data-testid={SIRUC_INSPECTIONS.formActividad}
              value={meta.actividad_sospechosa}
              onChange={(e) => setMeta({ ...meta, actividad_sospechosa: e.target.value })}
              className="input min-h-[70px]"
            />
          </Field>
        </section>

        <section className="bg-white border border-border p-5">
          <h2 className="font-heading text-lg font-bold text-navy mb-4">Firma de conformidad</h2>
          <div className="grid md:grid-cols-1 gap-6 max-w-sm">
            <SignaturePad
              label="Firma del Inspector *"
              value={meta.firma_inspector}
              onChange={(v) => setMeta({ ...meta, firma_inspector: v })}
              testid="siruc-firma-inspector"
            />
          </div>
        </section>

        <section className="bg-white border border-border p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-lg font-bold text-navy">Puntos de inspección</h2>
            <span className={`text-xs px-2 py-0.5 ${badCount > 0 ? "bg-critical/10 text-critical" : "bg-success/10 text-success"}`}>
              {badCount} malo(s) · {points.length - badCount} bueno(s)
            </span>
          </div>
          <div className="space-y-3">
            {points.map((p, idx) => (
              <div key={p.number} className="border border-border p-4">
                <div className="flex items-start justify-between gap-3 mb-3">
                  <div className="flex items-start gap-3">
                    <span className="w-7 h-7 bg-navy text-white flex items-center justify-center text-xs font-bold shrink-0">
                      {p.number}
                    </span>
                    <p className="text-sm font-medium text-navy pt-1">{p.name}{p.name_zh ? <span className="text-muted-foreground font-normal"> / {p.name_zh}</span> : ""}</p>
                  </div>
                  <div className="flex gap-1" data-testid={SIRUC_INSPECTIONS.pointEstado}>
                    {["bueno", "malo"].map((st) => (
                      <button
                        key={st}
                        type="button"
                        onClick={() => updatePoint(idx, { estado: st })}
                        className={`px-3 py-1.5 text-xs font-black uppercase tracking-widest border-2 transition-all ${
                          p.estado === st
                            ? st === "bueno"
                              ? "bg-success text-white border-success shadow-lg shadow-success/20"
                              : "bg-critical text-white border-critical shadow-lg shadow-critical/20"
                            : "bg-white text-muted-foreground border-border hover:border-navy"
                        }`}
                      >
                        {st === "bueno" ? "Bueno" : "Malo"}
                      </button>
                    ))}
                  </div>
                </div>
                <textarea
                  data-testid={SIRUC_INSPECTIONS.pointComentarios}
                  value={p.comentarios}
                  onChange={(e) => updatePoint(idx, { comentarios: e.target.value })}
                  placeholder="Comentarios o hallazgos..."
                  className="input min-h-[40px] text-sm"
                />

                {p.estado === "malo" && (
                  <div className="mt-3 p-3 bg-critical/5 border border-critical/20 rounded-lg animate-in fade-in slide-in-from-top-1">
                    <p className="text-[10px] font-black text-critical uppercase mb-2">Evidencia Fotográfica Obligatoria (Hallazgo)</p>
                    <PhotoCapture
                      single
                      label=""
                      value={p.photo ? [p.photo] : []}
                      onChange={(v) => updatePoint(idx, { photo: v[0] || "" })}
                      testid={`siruc-point-${p.number}`}
                    />
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <div className="flex gap-3">
          <button
            type="submit"
            data-testid={SIRUC_INSPECTIONS.formSubmit}
            disabled={saveMutation.isPending}
            className="h-12 px-6 bg-navy text-white font-semibold hover:bg-navy-hover disabled:opacity-50"
          >
            {saveMutation.isPending ? "Guardando…" : (inspId ? "Guardar cambios" : "Guardar inspección")}
          </button>
          <Link
            to="/siruc/inspections"
            className="h-12 px-6 border border-border text-navy hover:bg-secondary flex items-center"
          >
            Cancelar
          </Link>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="text-sm font-medium text-navy">{label}</label>
      {children}
    </div>
  );
}
